import { Injectable } from '@angular/core';
import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  Table,
  TableRow,
  TableCell,
  WidthType,
} from 'docx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { marked } from 'marked';
import type { Output } from '../../interfaces/task.interface';
import type { Document as SessionDocument } from '../../interfaces/document.interface';

/** Link rect in jsPDF coordinates (y = top). Used to add internal PDF link annotations after merge. */
export interface ExportLinkRect {
  url: string;
  pageIndex: number;
  x: number;
  y: number;
  w: number;
  h: number;
}

@Injectable({
  providedIn: 'root',
})
export class UtilsService {
  sanitizeFileName(name: string): string {
    if (!name) return name;
    const lastDot = name.lastIndexOf('.');
    if (lastDot <= 0) {
      // No extension or starts with dot; remove all dots
      return name.replaceAll(/\./g, '');
    }
    const base = name.slice(0, lastDot).replaceAll(/\./g, '');
    const ext = name.slice(lastDot); // includes the dot
    return base + ext;
  }

  /** Sanitize email for use in file names (replace @ and invalid path chars). */
  sanitizeEmailForFilename(email: string): string {
    if (!email?.trim()) return '';
    return email
      .trim()
      .replace(/@/g, '-at-')
      .replace(/[\\/:*?"<>|]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  }

  /**
   * Builds a descriptive download name in the form
   * `SessionName_basefile_YYYY_MM_DD` (no extension), or
   * `SessionName_basefile_variant_YYYY_MM_DD` when a report variant is given
   * (`follow_up` is written `follow-up`). The session name is sanitized for
   * filesystem safety; an empty session name drops the prefix.
   */
  buildReportFileName(
    sessionName: string | null | undefined,
    baseFile: string,
    variant?: string | null,
  ): string {
    const safeSession = (sessionName ?? '')
      .trim()
      .replace(/[\\/:*?"<>|\s]+/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '');
    const now = new Date();
    const datePart = `${now.getFullYear()}_${String(now.getMonth() + 1).padStart(2, '0')}_${String(now.getDate()).padStart(2, '0')}`;
    const prefix = safeSession ? `${safeSession}_` : '';
    const variantPart = variant ? `_${variant.replace(/_/g, '-')}` : '';
    return `${prefix}${baseFile}${variantPart}_${datePart}`;
  }

  MarkdownToPlainText(markdown: string) {
    try {
      const plainText = markdown
        ?.replaceAll(/<[^>]*>/g, '') // Remove HTML tags
        ?.replaceAll(/#{1,6}\s/g, '') // Remove headers
        ?.replaceAll(/\*\*/g, '') // Remove bold
        ?.replaceAll(/\*/g, '') // Remove italic
        ?.replaceAll(/`/g, '') // Remove code ticks
        ?.trim();

      return plainText;
    } catch (error) {
      console.error('Error in MarkdownToPlainText:', error);
      return '';
    }
  }

  async generateDocx(content: string, filename: string): Promise<void> {
    try {
      const cleaned = this.stripMarkdownLinksForExport(content ?? '');
      // Parse markdown content and convert to DOCX elements
      const docElements = this.parseMarkdownToDocx(cleaned);

      // Create document with formatted elements
      const doc = new Document({
        sections: [
          {
            properties: {},
            children: docElements,
          },
        ],
      });

      // Generate and download the DOCX file using toBlob for browser compatibility
      const blob = await Packer.toBlob(doc);

      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      link.click();

      globalThis.URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error('Error generating DOCX:', error);
      throw error;
    }
  }

  async generateHtml(content: string, filename: string): Promise<void> {
    try {
      let html = content || '';

      // If plaintext markdown, render to HTML; if already HTML, keep as-is
      const hasHtmlTags = /<\/?[a-z][\s\S]*>/i.test(html);
      if (!hasHtmlTags) {
        html = await marked(html);
      }

      const fullDocument = `<!DOCTYPE html>\n<html lang="en">\n<head>\n  <meta charset="UTF-8" />\n  <meta name="viewport" content="width=device-width, initial-scale=1.0" />\n  <title>AI Scribe Report</title>\n  <style>body{font-family:system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; padding:1rem; color:#222;} table{border-collapse:collapse;width:100%;} th,td{border:1px solid #ccc;padding:0.5rem;text-align:left;} </style>\n</head>\n<body>${html}</body>\n</html>`;

      const blob = new Blob([fullDocument], { type: 'text/html' });

      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      link.click();

      globalThis.URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error('Error generating HTML:', error);
      throw error;
    }
  }

  async generatePdf(content: string, filename: string): Promise<void> {
    try {
      const cleaned = this.stripMarkdownLinksForExport(content ?? '');
      const doc = new jsPDF({ unit: 'pt', format: 'a4' });

      const pageWidth = doc.internal.pageSize.getWidth();
      const marginLeft = 50;
      const marginRight = 50;
      const usableWidth = pageWidth - marginLeft - marginRight;
      const baseFontSize = 12;
      const lineHeight = 16;

      const lines = cleaned.split('\n');
      let cursorY = 60;

      const ensurePageSpace = (needed: number) => {
        const pageHeight = doc.internal.pageSize.getHeight();
        if (cursorY + needed > pageHeight - 60) {
          doc.addPage();
          cursorY = 60;
        }
      };

      const parseInlineBold = (
        text: string,
      ): { text: string; bold: boolean }[] => {
        const parts: { text: string; bold: boolean }[] = [];
        const boldPattern = /(\*\*|__)(.*?)\1/g;
        let last = 0;
        let m: RegExpExecArray | null;
        while ((m = boldPattern.exec(text)) !== null) {
          if (m.index > last)
            parts.push({ text: text.substring(last, m.index), bold: false });
          parts.push({ text: m[2], bold: true });
          last = m.index + m[0].length;
        }
        if (last < text.length)
          parts.push({ text: text.substring(last), bold: false });
        return parts.length ? parts : [{ text, bold: false }];
      };

      const measureTextWidth = (
        txt: string,
        fontSize: number,
        bold: boolean,
      ) => {
        doc.setFont('helvetica', bold ? 'bold' : 'normal');
        doc.setFontSize(fontSize);
        return doc.getTextWidth(txt);
      };

      const drawRichParagraph = (
        text: string,
        options?: { fontSize?: number; bold?: boolean; indent?: number },
      ) => {
        const fontSize = options?.fontSize ?? baseFontSize;
        const defaultBold = options?.bold ?? false;
        const indent = options?.indent ?? 0;

        // Split into runs, preserving bold
        const runs = parseInlineBold(text).map((r) => ({
          text: r.text,
          bold: r.bold || defaultBold,
        }));

        let x = marginLeft + indent;
        const maxWidth = usableWidth - indent;

        const flushLine = (segments: { text: string; bold: boolean }[]) => {
          ensurePageSpace(lineHeight);
          let xCursor = x;
          for (const seg of segments) {
            if (!seg.text) continue;
            doc.setFont('helvetica', seg.bold ? 'bold' : 'normal');
            doc.setFontSize(fontSize);
            doc.text(seg.text, xCursor, cursorY, { baseline: 'top' });
            xCursor += measureTextWidth(seg.text, fontSize, seg.bold);
          }
          cursorY += lineHeight;
        };

        // Build lines word-by-word taking styles into account
        let currentLine: { text: string; bold: boolean }[] = [];
        let currentWidth = 0;

        for (const run of runs) {
          const words = run.text.split(/(\s+)/); // keep spaces
          for (const w of words) {
            const wWidth = measureTextWidth(w, fontSize, run.bold);
            if (currentWidth + wWidth > maxWidth && currentLine.length > 0) {
              flushLine(currentLine);
              currentLine = [];
              currentWidth = 0;
            }
            currentLine.push({ text: w, bold: run.bold });
            currentWidth += wWidth;
          }
        }
        if (currentLine.length) flushLine(currentLine);
        cursorY += 4; // paragraph spacing
      };

      const parseTableRow = (line: string): string[] => {
        const cells = line.split('|').map((c) => c.trim());
        // Only remove the first and last elements if they're empty (artifacts from | at start/end)
        // but preserve empty cells in the middle of the table
        if (cells.length > 0 && cells[0] === '') cells.shift();
        const lastCell = cells.at(-1);
        if (cells.length > 0 && lastCell === '') cells.pop();
        return cells;
      };

      const isTableLine = (line: string) =>
        line.includes('|') && !line.startsWith('#');

      let i = 0;
      while (i < lines.length) {
        const raw = lines[i];
        const line = raw.trim();

        if (line === '') {
          cursorY += 6;
          i++;
          continue;
        }

        if (isTableLine(line)) {
          const tableLines: string[] = [];
          while (i < lines.length && isTableLine(lines[i].trim())) {
            tableLines.push(lines[i].trim());
            i++;
          }

          if (tableLines.length >= 2) {
            const header = parseTableRow(tableLines[0]);
            const body = tableLines.slice(2).map((r) => parseTableRow(r));

            ensurePageSpace(60);
            autoTable(doc, {
              startY: cursorY,
              head: [header],
              body,
              styles: { font: 'helvetica', fontSize: 11 },
              headStyles: {
                fillColor: [235, 235, 235],
                textColor: 0,
                fontStyle: 'bold',
              },
              margin: { left: marginLeft, right: marginRight },
              tableWidth: usableWidth,
            });
            cursorY = (doc as any).lastAutoTable.finalY + 10;
            continue;
          }
        }

        if (line.startsWith('#')) {
          const match = /^#+/.exec(line);
          const level = match?.[0].length || 1;
          const text = line.replace(/^#+\s*/, '');
          const sizes = [16, 14, 12, 11, 10, 9];
          drawRichParagraph(text, {
            bold: true,
            fontSize: sizes[Math.min(level - 1, sizes.length - 1)],
          });
          i++;
          continue;
        }

        if (line.startsWith('- ') || line.startsWith('* ')) {
          const item = line.replace(/^[-*]\s*/, '');
          // Draw bullet symbol at indent
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(baseFontSize);
          ensurePageSpace(lineHeight);
          doc.text('•', marginLeft, cursorY, { baseline: 'top' });
          drawRichParagraph(item, { fontSize: baseFontSize, indent: 14 });
          i++;
          continue;
        }

        if (/^\d+\.\s/.test(line)) {
          const match = /^(\d+)\.\s(.*)$/.exec(line);
          const label = match ? match[1] + '.' : '1.';
          const rest = match ? match[2] : line;
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(baseFontSize);
          ensurePageSpace(lineHeight);
          doc.text(label, marginLeft, cursorY, { baseline: 'top' });
          drawRichParagraph(rest, { fontSize: baseFontSize, indent: 18 });
          i++;
          continue;
        }

        // Handle horizontal rules (---)
        if (line.trim() === '---') {
          ensurePageSpace(20);
          // Draw a horizontal line
          doc.setDrawColor(200, 200, 200); // Light gray color
          doc.setLineWidth(1);
          doc.line(marginLeft, cursorY, pageWidth - marginRight, cursorY);
          cursorY += 12; // Add some space after the line
          i++;
          continue;
        }

        // Regular paragraph with inline bold; record header lines (date / " - " segment) in bold
        drawRichParagraph(line, {
          fontSize: baseFontSize,
          bold: this.isRecordHeaderLineForExport(line),
        });

        i++;
      }

      const blob = doc.output('blob');
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      link.click();
      globalThis.URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error('Error generating PDF:', error);
      throw error;
    }
  }

  /** Returns the PDF as bytes for merging (e.g. review of records + documents). */
  async generatePdfToArrayBuffer(content: string): Promise<Uint8Array> {
    const cleaned = this.stripMarkdownLinksForExport(content ?? '');
    const { doc } = this.buildPdfFromContent(cleaned);
    return new Uint8Array(doc.output('arraybuffer'));
  }

  /** Returns PDF bytes and link rects for adding internal destinations after merge. */
  async generatePdfWithLinkRects(
    content: string,
  ): Promise<{ bytes: Uint8Array; linkRects: ExportLinkRect[] }> {
    const { doc, linkRects } = this.buildPdfFromContent(content);
    const bytes = new Uint8Array(doc.output('arraybuffer'));
    return { bytes, linkRects };
  }

  /** Record header lines (date and/or " - " segment); used for bold in export PDFs. */
  private isRecordHeaderLineForExport(text: string): boolean {
    const plain = text.replaceAll(/\[([^\]]*)\]\([^)]*\)/g, '$1').trim();
    if (!plain) return false;
    const startsWithDate = /^\d{1,2}\/\d{1,2}\/\d{4}\s+/.test(plain);
    const hasSegmentDash = /\s+-\s+/.test(plain);
    return startsWithDate || hasSegmentDash;
  }

  private buildPdfFromContent(content: string): {
    doc: jsPDF;
    linkRects: ExportLinkRect[];
  } {
    const doc = new jsPDF({ unit: 'pt', format: 'a4' });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const marginLeft = 50;
    const marginRight = 50;
    const marginTop = 60;
    const marginBottom = 60;
    const usableWidth = pageWidth - marginLeft - marginRight;
    const baseFontSize = 12;
    const lineHeight = 16;

    const lines = content.split('\n');
    let cursorY = marginTop;
    const linkRects: ExportLinkRect[] = [];

    const ensurePageSpace = (needed: number) => {
      if (cursorY + needed > pageHeight - marginBottom) {
        doc.addPage();
        cursorY = marginTop;
      }
    };

    const getCurrentPageIndex = (): number => {
      const info = (doc as any).internal?.getCurrentPageInfo?.();
      return info?.pageNumber == null ? 0 : info.pageNumber - 1;
    };

    const parseInlineBold = (
      text: string,
    ): { text: string; bold: boolean }[] => {
      const parts: { text: string; bold: boolean }[] = [];
      const boldPattern = /(\*\*|__)(.*?)\1/g;
      let last = 0;
      let m: RegExpExecArray | null;
      while ((m = boldPattern.exec(text)) !== null) {
        if (m.index > last)
          parts.push({ text: text.substring(last, m.index), bold: false });
        parts.push({ text: m[2], bold: true });
        last = m.index + m[0].length;
      }
      if (last < text.length)
        parts.push({ text: text.substring(last), bold: false });
      return parts.length ? parts : [{ text, bold: false }];
    };

    /** Parse line into segments; markdown [text](url) becomes a segment with linkUrl. */
    const parseLineWithLinks = (
      line: string,
    ): { text: string; bold: boolean; linkUrl?: string }[] => {
      const segments: { text: string; bold: boolean; linkUrl?: string }[] = [];
      const linkRegex = /\[([^\]]*)\]\(([^)]*)\)/g;
      let last = 0;
      let m: RegExpExecArray | null;
      while ((m = linkRegex.exec(line)) !== null) {
        if (m.index > last) {
          const before = line.substring(last, m.index);
          parseInlineBold(before).forEach((p) =>
            segments.push({ ...p, linkUrl: undefined }),
          );
        }
        segments.push({
          text: m[1],
          bold: false,
          linkUrl: m[2].trim() || undefined,
        });
        last = m.index + m[0].length;
      }
      if (last < line.length) {
        parseInlineBold(line.substring(last)).forEach((p) =>
          segments.push({ ...p, linkUrl: undefined }),
        );
      }
      return segments.length ? segments : [{ text: line, bold: false }];
    };

    const measureTextWidth = (txt: string, fontSize: number, bold: boolean) => {
      doc.setFont('helvetica', bold ? 'bold' : 'normal');
      doc.setFontSize(fontSize);
      return doc.getTextWidth(txt);
    };

    type SegmentWithLink = {
      text: string;
      bold: boolean;
      linkUrl?: string;
    };

    const flushLine = (
      segments: SegmentWithLink[],
      lineFontSize: number,
      lineIndent: number,
    ) => {
      ensurePageSpace(lineHeight);
      const pageIndex = getCurrentPageIndex();
      let xCursor = marginLeft + lineIndent;
      for (const seg of segments) {
        if (!seg.text) continue;
        const w = measureTextWidth(seg.text, lineFontSize, seg.bold);
        doc.setFont('helvetica', seg.bold ? 'bold' : 'normal');
        doc.setFontSize(lineFontSize);
        if (seg.linkUrl) {
          doc.setTextColor(0, 0, 238); // blue so users know it's clickable
        } else {
          doc.setTextColor(0, 0, 0);
        }
        doc.text(seg.text, xCursor, cursorY, { baseline: 'top' });
        if (seg.linkUrl) {
          linkRects.push({
            url: seg.linkUrl,
            pageIndex,
            x: xCursor,
            y: cursorY,
            w,
            h: lineHeight,
          });
        }
        xCursor += w;
      }
      doc.setTextColor(0, 0, 0);
      cursorY += lineHeight;
    };

    const drawRichParagraph = (
      text: string,
      options?: { fontSize?: number; bold?: boolean; indent?: number },
    ) => {
      const fontSize = options?.fontSize ?? baseFontSize;
      const defaultBold = options?.bold ?? false;
      const indent = options?.indent ?? 0;

      const runs = parseLineWithLinks(text).map((r) => ({
        text: r.text,
        bold: r.bold || defaultBold,
        linkUrl: r.linkUrl,
      }));

      const maxWidth = usableWidth - indent;

      let currentLine: SegmentWithLink[] = [];
      let currentWidth = 0;

      for (const run of runs) {
        const words = run.text.split(/(\s+)/);
        for (const w of words) {
          const wWidth = measureTextWidth(w, fontSize, run.bold);
          if (currentWidth + wWidth > maxWidth && currentLine.length > 0) {
            flushLine(currentLine, fontSize, indent);
            currentLine = [];
            currentWidth = 0;
          }
          currentLine.push({
            text: w,
            bold: run.bold,
            linkUrl: run.linkUrl,
          });
          currentWidth += wWidth;
        }
      }
      if (currentLine.length) flushLine(currentLine, fontSize, indent);
      cursorY += 4;
    };

    const parseTableRow = (line: string): string[] => {
      const cells = line.split('|').map((c) => c.trim());
      // Only remove the first and last elements if they're empty (artifacts from | at start/end)
      // but preserve empty cells in the middle of the table
      if (cells.length > 0 && cells[0] === '') cells.shift();
      const lastCell = cells.at(-1);
      if (cells.length > 0 && lastCell === '') cells.pop();
      return cells;
    };

    const isTableLine = (line: string) =>
      line.includes('|') && !line.startsWith('#');

    let i = 0;
    while (i < lines.length) {
      const raw = lines[i];
      const line = raw.trim();

      if (line === '') {
        cursorY += 6;
        i++;
        continue;
      }

      if (isTableLine(line)) {
        const tableLines: string[] = [];
        while (i < lines.length && isTableLine(lines[i].trim())) {
          tableLines.push(lines[i].trim());
          i++;
        }

        if (tableLines.length >= 2) {
          const header = parseTableRow(tableLines[0]);
          const body = tableLines.slice(2).map((r) => parseTableRow(r));

          ensurePageSpace(60);
          autoTable(doc, {
            startY: cursorY,
            head: [header],
            body,
            styles: { font: 'helvetica', fontSize: 11 },
            headStyles: {
              fillColor: [235, 235, 235],
              textColor: 0,
              fontStyle: 'bold',
            },
            margin: { left: marginLeft, right: marginRight },
            tableWidth: usableWidth,
          });
          cursorY = (doc as any).lastAutoTable.finalY + 10;
          continue;
        }
      }

      if (line.startsWith('#')) {
        const match = /^#+/.exec(line);
        const level = match?.[0].length || 1;
        const text = line.replace(/^#+\s*/, '');
        const sizes = [16, 14, 12, 11, 10, 9];
        drawRichParagraph(text, {
          bold: true,
          fontSize: sizes[Math.min(level - 1, sizes.length - 1)],
        });
        i++;
        continue;
      }

      if (line.startsWith('- ') || line.startsWith('* ')) {
        const item = line.replace(/^[-*]\s*/, '');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(baseFontSize);
        ensurePageSpace(lineHeight);
        doc.text('•', marginLeft, cursorY, { baseline: 'top' });
        drawRichParagraph(item, { fontSize: baseFontSize, indent: 14 });
        i++;
        continue;
      }

      if (/^\d+\.\s/.test(line)) {
        const match = /^(\d+)\.\s(.*)$/.exec(line);
        const label = match ? match[1] + '.' : '1.';
        const rest = match ? match[2] : line;
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(baseFontSize);
        ensurePageSpace(lineHeight);
        doc.text(label, marginLeft, cursorY, { baseline: 'top' });
        drawRichParagraph(rest, { fontSize: baseFontSize, indent: 18 });
        i++;
        continue;
      }

      if (line.trim() === '---') {
        ensurePageSpace(20);
        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(1);
        doc.line(marginLeft, cursorY, pageWidth - marginRight, cursorY);
        cursorY += 12;
        i++;
        continue;
      }

      drawRichParagraph(line, {
        fontSize: baseFontSize,
        bold: this.isRecordHeaderLineForExport(line),
      });
      i++;
    }

    return { doc, linkRects };
  }

  buildReviewOfRecordsContentForExport(
    rawContent: string,
    options: {
      reviewedBy: string;
      stripHyperlinks?: boolean;
      /** When true, wrap record header lines (e.g. "01/01/2025 Patient Health Summary - ...") in ** so DOCX export renders them bold like PDF. */
      boldRecordHeaderLines?: boolean;
    },
  ): string {
    if (!rawContent?.trim()) return rawContent;
    let content =
      options.stripHyperlinks === true
        ? this.removeHyperlinksFromTextForExport(rawContent)
        : rawContent;
    // Normalize title to "Records Review" in export
    content = content.replaceAll(/^\s*Record Review\s*$/gm, 'Records Review');
    content = content.replaceAll(
      /\bEnd of Record Review\./gi,
      'End of Records Review.',
    );
    const totalPages = this.parseTotalPagesFromReviewContent(content) || '0';
    const duplicatePages =
      this.parsePageCountFromReviewContent(
        content,
        this.getPageCountPatternsForLabel('Duplicate Pages'),
      ) || '0';
    const blankPages =
      this.parsePageCountFromReviewContent(
        content,
        this.getPageCountPatternsForLabel('Blank Pages'),
      ) || '0';
    let billablePages =
      this.parsePageCountFromReviewContent(
        content,
        this.getPageCountPatternsForLabel('Billable Pages'),
      ) || '';
    if (!billablePages) {
      const t = Number.parseInt(totalPages, 10) || 0;
      const d = Number.parseInt(duplicatePages, 10) || 0;
      const b = Number.parseInt(blankPages, 10) || 0;
      billablePages = t > 0 ? String(Math.max(0, t - d - b)) : '0';
    }
    const dateOfInjury =
      this.parseDateOfInjuryFromReviewContent(content) || 'N/A';
    content = this.buildReviewOfRecordsHeaderAndBody(
      content,
      totalPages,
      duplicatePages,
      blankPages,
      billablePages,
      dateOfInjury,
      options.reviewedBy,
    );
    content = this.boldSectionLabelsForExport(content);
    if (options.boldRecordHeaderLines === true) {
      content = this.boldRecordHeaderLinesForExport(content);
    }
    const summaryLine = `\n\nSummarized by **EvalPath (LongHealth)**, for **${options.reviewedBy}**`;

    // Match only the exact line "End of Review of Records." (no other variants)
    const exactEndLine = 'End of Review of Records.';
    const lastIdx = content.lastIndexOf(exactEndLine);
    if (lastIdx !== -1) {
      const beforeLast = content.substring(0, lastIdx);
      const afterLast = content.substring(lastIdx + exactEndLine.length);
      const boldEndText = `**${exactEndLine}**`;
      const horizontalLine = '\n\n---\n\n';
      content =
        beforeLast + horizontalLine + boldEndText + summaryLine + afterLast;
    }

    return content;
  }

  private removeHyperlinksFromTextForExport(content: string): string {
    if (!content) return '';
    return content.replaceAll(/\[([^\]]*)\]\([^)]*\)/g, '$1');
  }

  /** Remove HTML anchor tags, keeping inner text (for export when UI uses &lt;a href&gt;). */
  private stripHtmlAnchorsForExport(content: string): string {
    if (!content) return '';
    return content.replace(/<a\b[^>]*>([\s\S]*?)<\/a>/gi, '$1');
  }

  /**
   * Summary payloads occasionally begin with `# Quick Reference` (or same under ##–######).
   * Section parsing in the UI only structures ##/### blocks, so that H1 line is easy to miss
   * on-screen while DOCX/PDF export uses the full raw markdown.
   */
  normalizeMislabeledSummaryFirstHeading(content: string): string {
    if (!content) return content;
    const lines = content.split('\n');
    let i = 0;
    while (i < lines.length && lines[i].trim() === '') i++;
    if (i >= lines.length) return content;
    const m = lines[i].match(/^(\s*)(#{1,6}\s+)Quick Reference(\s*)$/i);
    if (!m) return content;
    lines[i] = `${m[1]}${m[2]}Summary${m[3]}`;
    return lines.join('\n');
  }

  /**
   * Strip markdown [text](url) and simple HTML &lt;a&gt; tags for DOCX/PDF download exports.
   * Applied inside generateDocx / generatePdf so tab downloads stay consistent (e.g. Summary).
   */
  stripMarkdownLinksForExport(content: string): string {
    let s = this.removeHyperlinksFromTextForExport(content ?? '');
    s = this.stripHtmlAnchorsForExport(s);
    return s;
  }

  /**
   * Resolve an export link URL (e.g. "some_report_page_12.txt?page=3") to the 0-based
   * merged page index. Trims .txt, matches to PDF doc by base filename, extracts page from
   * ?page=N or from filename (page_12, p12, _12). Returns null if no match.
   */
  resolveExportLinkToMergedPage(
    url: string,
    pdfDocumentPaths: string[],
    pageOffsets: number[],
    firstOriginalPartIndex: number,
  ): number | null {
    if (!url?.trim() || pdfDocumentPaths.length === 0) return null;
    const s = url.trim();
    const q = s.indexOf('?');
    const pathPart = q >= 0 ? s.substring(0, q) : s;
    let pageFromQuery: number | null = null;
    if (q >= 0) {
      const pageMatch = /[?&]page=(\d+)/.exec(s);
      if (pageMatch) pageFromQuery = Number.parseInt(pageMatch[1], 10);
    }
    const baseName = pathPart
      .replace(/\.(txt|pdf|jpg|jpeg|png|tiff|tif)$/i, '')
      .trim();
    if (!baseName) return null;
    const baseLower = baseName.toLowerCase();
    const norm = (p: string) =>
      p
        .replaceAll('\\', '/')
        .split('/')
        .pop()
        ?.replace(/\.(pdf|jpg|jpeg|png|tiff|tif)$/i, '')
        .toLowerCase() ?? '';
    let docIndex = -1;
    for (let i = 0; i < pdfDocumentPaths.length; i++) {
      if (norm(pdfDocumentPaths[i]) === baseLower) {
        docIndex = i;
        break;
      }
    }
    if (docIndex < 0) {
      for (let i = 0; i < pdfDocumentPaths.length; i++) {
        const fileBase = norm(pdfDocumentPaths[i]);
        if (fileBase.includes(baseLower) || baseLower.includes(fileBase)) {
          docIndex = i;
          break;
        }
      }
    }
    if (docIndex < 0) return null;
    const partIndex = firstOriginalPartIndex + docIndex;
    if (partIndex >= pageOffsets.length) return null;
    let localPage = 1;
    if (pageFromQuery != null && pageFromQuery > 0) localPage = pageFromQuery;
    else {
      const pageInName = /(?:^|_)(?:page_?)?(\d+)$/i.exec(baseName);
      if (pageInName) localPage = Number.parseInt(pageInName[1], 10);
    }
    const startPage = pageOffsets[partIndex];
    const nextStart =
      partIndex + 1 < pageOffsets.length
        ? pageOffsets[partIndex + 1]
        : startPage + 999;
    const maxPages = nextStart - startPage;
    const oneBased = Math.min(localPage, maxPages);
    return startPage + oneBased - 1;
  }

  private parseTotalPagesFromReviewContent(content: string): string {
    const patterns = [
      /###?\s+\*\*Total Pages Reviewed\*\*\s*\n(\d+)/i,
      /\*\*Total Pages Reviewed:\*\*\s*(\d+)/i,
      /Total Pages Reviewed[:\s]+(\d+)/i,
    ];
    for (const p of patterns) {
      const m = p.exec(content);
      if (m) return m[1];
    }
    return '';
  }

  private parsePageCountFromReviewContent(
    content: string,
    patterns: RegExp[],
  ): string {
    if (!content) return '';
    for (const p of patterns) {
      const m = p.exec(content);
      if (m?.[1]) return m[1];
    }
    return '';
  }

  /** Returns regex patterns to parse a page count for a given label (e.g. "Duplicate Pages", "Blank Pages", "Billable Pages"). */
  private getPageCountPatternsForLabel(label: string): RegExp[] {
    return [
      new RegExp(`###?\\s+\\*\\*${label}\\*\\*\\s*\\n(\\d+)`, 'i'),
      new RegExp(`\\*\\*${label}:\\*\\*\\s*(\\d+)`, 'i'),
      new RegExp(`${label}[:\\s]+(\\d+)`, 'i'),
    ];
  }

  private parseDateOfInjuryFromReviewContent(content: string): string {
    const patterns = [
      /###?\s+\*\*Date\s+of\s+Injury\s*\(DOI\)\*\*[:\-]?\s*\n([^\n]+)/i,
      /###?\s+Date\s+of\s+Injury\s*\(DOI\)[:\-]?\s*\n([^\n]+)/i,
      /Date\s+of\s+Injury[:\-]?\s*([A-Za-z]{3}\s+\d{1,2},?\s+\d{4}|\d{1,2}\/\d{1,2}\/\d{4}|\d{4}-\d{1,2}-\d{1,2})/i,
      /DOI[:\-]?\s*([A-Za-z]{3}\s+\d{1,2},?\s+\d{4}|\d{1,2}\/\d{1,2}\/\d{4}|\d{4}-\d{1,2}-\d{1,2})/i,
    ];
    for (const p of patterns) {
      const m = p.exec(content);
      if (m) return m[1].trim();
    }
    return '';
  }

  private buildReviewOfRecordsHeaderAndBody(
    content: string,
    totalPages: string,
    duplicatePages: string,
    blankPages: string,
    billablePages: string,
    dateOfInjury: string,
    reviewedBy: string,
  ): string {
    const patientMatch = /^Patient:\s*(.+)$/im.exec(content);
    const patientName = patientMatch?.[1]?.trim() ?? 'N/A';
    const lines = content.split('\n');
    const recordLineIndex = lines.findIndex((line) =>
      /^\d{1,2}\/\d{1,2}\/\d{4}/.test(line.trim()),
    );
    const recordsBody =
      recordLineIndex >= 0 ? lines.slice(recordLineIndex).join('\n') : content;
    const header =
      `**Patient:** ${patientName}\n` +
      `**Date of Injury (DOI):** ${dateOfInjury}\n\n\n` +
      `# REVIEW OF MEDICAL RECORDS\n\n` +
      `**Reviewed by:** ${reviewedBy}; **Total Pages:** ${totalPages}; **Duplicate Pages:** ${duplicatePages}; **Blank Pages:** ${blankPages}; **Billable Pages:** ${billablePages}.\n\n`;
    return header + recordsBody;
  }

  private boldSectionLabelsForExport(content: string): string {
    if (!content) return '';
    const sectionLabels =
      /^\s*(Psych Symptoms|Psych Dx|Psych Tx|Treatment History|Prescription Medications|Medications|Job Description|Work History|Work Status|Social History|Medical History|Comparison|Findings|Impression|Labs|Vitals|HPI|CC|DX|TX)\s*:/i;
    return content
      .split('\n')
      .map((line) => {
        const m = sectionLabels.exec(line);
        if (m) {
          const label = m[1].trim();
          const rest = line.slice(m[0].length);
          return `**${label}:**${rest}`;
        }
        return line;
      })
      .join('\n');
  }

  /**
   * Wraps record header lines (e.g. "01/01/2025 Patient Health Summary - Rohit Patil, Primary Office") in **
   * so DOCX export renders them bold, matching the PDF review-of-records export.
   */
  private boldRecordHeaderLinesForExport(content: string): string {
    if (!content) return '';
    return content
      .split('\n')
      .map((line) => {
        if (this.isRecordHeaderLineForExport(line)) {
          return `**${line.trim()}**`;
        }
        return line;
      })
      .join('\n');
  }

  private parseMarkdownToDocx(content: string): (Paragraph | Table)[] {
    const lines = content.split('\n');
    const elements: (Paragraph | Table)[] = [];
    let i = 0;

    while (i < lines.length) {
      const line = lines[i].trim();

      if (line === '') {
        // Add empty paragraph for spacing
        elements.push(new Paragraph({ children: [new TextRun('')] }));
        i++;
        continue;
      }

      // Check if this line starts a table (contains |)
      if (line.includes('|') && !line.startsWith('#')) {
        const tableResult = this.parseTable(lines, i);
        if (tableResult.table) {
          elements.push(tableResult.table);
          i = tableResult.nextIndex;
          continue;
        }
      }

      // Handle headers (# ## ### #### ##### ######)
      if (line.startsWith('#')) {
        const match = /^#+/.exec(line);
        const headerLevel = match?.[0].length || 1;
        const headerText = line.replace(/^#+\s*/, '');

        // Parse inline formatting within headers
        const children = this.parseInlineFormatting(
          headerText,
          this.getHeaderSize(headerLevel),
          true,
        );
        elements.push(new Paragraph({ children }));
        i++;
        continue;
      }

      // Handle bullet points (- or *)
      if (line.startsWith('- ') || line.startsWith('* ')) {
        const bulletText = line.replace(/^[-*]\s*/, '');
        const children = this.parseInlineFormatting(`• ${bulletText}`, 24);
        elements.push(new Paragraph({ children }));
        i++;
        continue;
      }

      // Handle numbered lists
      if (/^\d+\.\s/.exec(line)) {
        const children = this.parseInlineFormatting(line, 24);
        elements.push(new Paragraph({ children }));
        i++;
        continue;
      }

      // Handle horizontal rules (---)
      if (line.trim() === '---') {
        elements.push(
          new Paragraph({
            children: [
              new TextRun({
                text: '─'.repeat(50), // Unicode horizontal line character
                size: 24,
                color: '808080', // Gray color
              }),
            ],
            spacing: { before: 200, after: 200 }, // Add spacing before and after
          }),
        );
        i++;
        continue;
      }

      // Handle any line with inline formatting (bold, italic, etc.)
      if (
        line.includes('**') ||
        line.includes('__') ||
        line.includes('*') ||
        line.includes('_')
      ) {
        const children = this.parseInlineFormatting(line, 24);
        elements.push(new Paragraph({ children }));
        i++;
        continue;
      }

      // Regular paragraph
      elements.push(
        new Paragraph({
          children: [
            new TextRun({
              text: line,
              size: 24,
            }),
          ],
        }),
      );
      i++;
    }

    return elements;
  }

  private parseInlineFormatting(
    text: string,
    fontSize: number = 24,
    isBold: boolean = false,
  ): TextRun[] {
    const children: TextRun[] = [];
    let remainingText = text;

    // Parse bold text (**text** or __text__)
    const boldRegex = /(\*\*|__)(.*?)\1/g;
    let lastIndex = 0;
    let match;

    while ((match = boldRegex.exec(remainingText)) !== null) {
      // Add text before bold
      if (match.index > lastIndex) {
        children.push(
          new TextRun({
            text: remainingText.substring(lastIndex, match.index),
            size: fontSize,
            bold: isBold,
          }),
        );
      }

      // Add bold text
      children.push(
        new TextRun({
          text: match[2],
          bold: true,
          size: fontSize,
        }),
      );

      lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < remainingText.length) {
      children.push(
        new TextRun({
          text: remainingText.substring(lastIndex),
          size: fontSize,
          bold: isBold,
        }),
      );
    }

    return children.length > 0
      ? children
      : [new TextRun({ text, size: fontSize, bold: isBold })];
  }

  private getHeaderSize(level: number): number {
    const sizes = [32, 28, 24, 22, 20, 18]; // H1 to H6
    return sizes[Math.min(level - 1, sizes.length - 1)];
  }

  private parseTable(
    lines: string[],
    startIndex: number,
  ): { table: Table | null; nextIndex: number } {
    const tableLines: string[] = [];
    let i = startIndex;

    // Collect all table lines
    while (i < lines.length) {
      const line = lines[i].trim();
      if (line.includes('|')) {
        tableLines.push(line);
        i++;
      } else {
        break;
      }
    }

    if (tableLines.length < 2) {
      return { table: null, nextIndex: startIndex + 1 };
    }

    // Parse header row
    const headerRow = this.parseTableRow(tableLines[0]);
    if (headerRow.length === 0) {
      return { table: null, nextIndex: startIndex + 1 };
    }

    // Skip separator row (| --- | --- |)
    const dataRows = tableLines.slice(2).map((row) => this.parseTableRow(row));

    // Create table rows
    const tableRows: TableRow[] = [];

    // Header row (bold)
    const headerCells = headerRow.map(
      (cell) =>
        new TableCell({
          children: [
            new Paragraph({
              children: [
                new TextRun({
                  text: cell,
                  bold: true,
                  size: 24,
                }),
              ],
            }),
          ],
        }),
    );
    tableRows.push(new TableRow({ children: headerCells }));

    // Data rows
    dataRows.forEach((row) => {
      if (row.length > 0) {
        const cells = row.map(
          (cell) =>
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: cell,
                      size: 24,
                    }),
                  ],
                }),
              ],
            }),
        );
        tableRows.push(new TableRow({ children: cells }));
      }
    });

    // Create table with proper column widths
    const columnWidth = Math.floor(10000 / headerRow.length); // Distribute width evenly
    const table = new Table({
      rows: tableRows,
      width: {
        size: 100,
        type: WidthType.PERCENTAGE,
      },
      columnWidths: new Array(headerRow.length).fill(columnWidth),
    });

    return { table, nextIndex: i };
  }

  private parseTableRow(line: string): string[] {
    // Split by | and clean up cells
    const cells = line.split('|').map((cell) => cell.trim());
    // Only remove the first and last elements if they're empty (artifacts from | at start/end)
    // but preserve empty cells in the middle of the table
    if (cells.length > 0 && cells[0] === '') cells.shift();
    const lastCell = cells.at(-1);
    if (cells.length > 0 && lastCell === '') cells.pop();
    return cells;
  }

  areAllPageCountsLoaded(documents: any[]): boolean {
    if (!documents || documents.length === 0) return true;
    return documents.every((doc) => {
      if (!doc) return true;
      const visionStatus = String(doc.visionStatus || '').toUpperCase();
      if (visionStatus === 'PENDING' || visionStatus === 'PROCESSING') {
        return false;
      }
      if (doc.ocrOutput) {
        const pages = doc.ocrOutput?.numberOfPages;
        return typeof pages === 'number' && pages > 0;
      }
      return false;
    });
  }

  private toTimestamp(value: Date | string | undefined): number | null {
    if (!value) return null;
    const time =
      value instanceof Date ? value.getTime() : new Date(value).getTime();
    return Number.isNaN(time) ? null : time;
  }

  /**
   * Returns true when an output was produced after the latest document/OCR update.
   * Use this to prevent showing stale outputs after new documents are uploaded.
   */
  isOutputFreshForCurrentDocuments(
    output: Pick<Output, 'createdAt' | 'updatedAt'> | null | undefined,
    documents: Array<SessionDocument | null | undefined>,
  ): boolean {
    if (!output) return false;

    const outputTime = Math.max(
      this.toTimestamp(output.updatedAt) ?? 0,
      this.toTimestamp(output.createdAt) ?? 0,
    );

    // If output timestamps are missing, preserve existing behavior.
    if (outputTime === 0) return true;

    const latestSourceTime = (documents || []).reduce((latest, doc) => {
      const docCreated = this.toTimestamp(doc?.createdAt) ?? 0;
      const ocrCreated = this.toTimestamp(doc?.ocrOutput?.createdAt) ?? 0;
      const ocrUpdated = this.toTimestamp(doc?.ocrOutput?.updatedAt) ?? 0;
      return Math.max(latest, docCreated, ocrCreated, ocrUpdated);
    }, 0);

    return outputTime >= latestSourceTime;
  }
}
